import { memo, useCallback, useState } from 'react';

import styled from 'styled-components';

import { Button, DateRangeFilter } from 'core/ui';
import { ButtonVariants } from 'core/ui/Button/constants';
import type { DateRangeFilterValue } from 'core/ui/DateRangeFilter';
import { ComponentSizes } from 'core/ui/constants';

export interface DateColumnFilterProps {
  /**
   * Current filter value
   */
  value?: DateRangeFilterValue | null;

  /**
   * Called when a filter is applied
   */
  onChange: (value: DateRangeFilterValue | null) => void;

  /**
   * Column title for the filter modal
   */
  columnTitle?: string;

  /**
   * Size of the filter components
   */
  size?: ComponentSizes;

  /**
   * Whether the filter is disabled
   */
  disabled?: boolean;

  /**
   * Custom icon for the filter button
   */
  icon?: string;
}

export const DateColumnFilter = memo<DateColumnFilterProps>(
  ({ value, onChange, columnTitle = 'Date', size = ComponentSizes.SMALL, disabled = false, icon = 'filter' }) => {
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    const handleOpenFilter = useCallback(() => {
      setIsFilterOpen(true);
    }, []);

    const handleCloseFilter = useCallback(() => {
      setIsFilterOpen(false);
    }, []);

    const handleFilterChange = useCallback(
      (filterValue: DateRangeFilterValue) => {
        onChange(filterValue);
      },
      [onChange],
    );

    const handleFilterClear = useCallback(() => {
      onChange(null);
    }, [onChange]);

    const hasActiveFilter = Boolean(value);

    return (
      <>
        <FilterButton
          variant={hasActiveFilter ? ButtonVariants.PRIMARY : ButtonVariants.SECONDARY}
          size={size}
          onClick={handleOpenFilter}
          disabled={disabled}
          title={`Filter ${columnTitle}`}
        >
          <FilterIcon className={`k-icon k-i-${icon}`} />
          {hasActiveFilter && <ActiveFilterIndicator />}
        </FilterButton>

        <DateRangeFilter
          isOpen={isFilterOpen}
          onClose={handleCloseFilter}
          value={value}
          onChange={handleFilterChange}
          onClear={handleFilterClear}
          title={`Filter ${columnTitle}`}
          size={size}
          disabled={disabled}
        />
      </>
    );
  },
);

DateColumnFilter.displayName = 'DateColumnFilter';

const FilterButton = styled(Button)`
  position: relative;
  min-width: 32px;
  padding: ${({ theme }) => theme.space.spacing10};
`;

const FilterIcon = styled.span`
  font-size: 14px;
  line-height: 1;
`;

const ActiveFilterIndicator = styled.div`
  position: absolute;
  top: 2px;
  right: 2px;
  width: 6px;
  height: 6px;
  background-color: ${({ theme }) => theme.colors.palette.blues[3]};
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.colors.palette.grayscale[0]};
`;
